import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownWideNarrow,
  Download,
  RotateCcw,
  Search,
  ExternalLink,
  PencilLine,
  CheckCircle2,
  AlertTriangle,
  Tag,
  Sparkles,
  X,
  TrendingDown,
  TrendingUp,
  Barcode,
  Layers,
  Percent,
} from 'lucide-react';
import {
  fetchCompetitorPrices,
  updateCompetitorProductPrice,
  me,
  type CompetitorBadge,
  type CompetitorRow,
  type CompetitorSourceMeta,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { int } from '../lib/format.js';
import { cn } from '../ui/cn.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

const SHORT_LABELS: Readonly<Record<string, string>> = {
  'pharma-shop': 'Pharma-Shop',
  parafendri: 'Parafendri',
  parashop: 'Parashop',
};

function shortLabel(source: CompetitorSourceMeta): string {
  return SHORT_LABELS[source.key] ?? source.label;
}

/** Formats integer millimes into Tunisian Dinar with exact 3 decimals. */
export function formatMillimes(val: number | null | undefined, fallback = '—'): string {
  if (val === null || val === undefined || Number.isNaN(val)) return fallback;
  return `${(val / 1000).toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`;
}

/** Formats signed gap in millimes to Dinars (+X,XXX DT or -X,XXX DT). */
export function formatSignedGap(gapMillimes: number | null | undefined): string {
  if (gapMillimes === null || gapMillimes === undefined || Number.isNaN(gapMillimes)) return '—';
  const sign = gapMillimes > 0 ? '+' : gapMillimes < 0 ? '-' : '';
  const abs = Math.abs(gapMillimes) / 1000;
  return `${sign}${abs.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`;
}

function badgeClass(badge: CompetitorBadge | null): string {
  if (badge === 'leader') return 'bg-emerald-50 text-emerald-950 border-emerald-300 ring-1 ring-emerald-400/30';
  if (badge === 'aligned') return 'bg-sky-50 text-sky-950 border-sky-300 ring-1 ring-sky-400/30';
  if (badge === 'above') return 'bg-rose-50 text-rose-950 border-rose-300 ring-1 ring-rose-400/30';
  return 'bg-slate-100 text-slate-800 border-slate-300 font-semibold';
}

function badgeLabel(badge: CompetitorBadge | null): string {
  if (badge === 'leader') return '🏆 Moins cher';
  if (badge === 'aligned') return '🎯 Aligné ±2 %';
  if (badge === 'above') return '⚠️ Plus cher';
  return '—';
}

function pct(n: number | null): string {
  if (n === null) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(1)} %`;
}

/** Competitor price vs ours, signed so a glance tells who is cheaper. */
function VsOurs({ ours, theirs }: { ours: number | null; theirs: number }): React.ReactElement {
  if (ours === null || ours <= 0) return <span className="text-ink-ghost">—</span>;
  const delta = Math.round(((theirs - ours) / ours) * 1000) / 10;
  if (delta === 0) return <span className="text-ink-ghost font-medium">=</span>;
  return (
    <span
      className={cn(
        'font-bold inline-flex items-center gap-0.5 text-[11px]',
        delta > 0 ? 'text-emerald-700' : 'text-rose-700'
      )}
    >
      {delta > 0 ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />}
      {delta > 0 ? '+' : ''}
      {delta.toFixed(1)} %
    </span>
  );
}

function KpiCard(props: {
  label: string;
  value: string;
  sub: string;
  accent: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}): React.ReactElement {
  return (
    <div
      onClick={props.onClick}
      className={cn(
        'rounded-xl border bg-card p-4 shadow-xs transition-all',
        props.onClick ? 'cursor-pointer hover:border-accent hover:shadow-sm' : '',
        props.active ? 'ring-2 ring-accent border-accent bg-accent/5' : 'border-border'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-ink-ghost">{props.label}</div>
        {props.icon}
      </div>
      <div className={cn('mt-1.5 text-2xl font-black tabular-nums tracking-tight', props.accent)}>{props.value}</div>
      <div className="mt-0.5 text-xs text-ink-soft">{props.sub}</div>
    </div>
  );
}

function share(count: number, total: number): string {
  if (total <= 0) return '—';
  return `${Math.round((count / total) * 100)} %`;
}

// ---------------------------------------------------------------------------
// Modale d'Ajustement de Prix en Direct sur WooCommerce
// ---------------------------------------------------------------------------

interface AdjustPriceModalProps {
  row: CompetitorRow;
  onClose: () => void;
  onSuccess: (updated: {
    id: number;
    regularPriceTtc: number | null;
    salePriceTtc: number | null;
    ourPrice: number | null;
  }) => void;
}

function AdjustPriceModal({ row, onClose, onSuccess }: AdjustPriceModalProps): React.ReactElement {
  const currentRegDinar = row.regularPriceTtc ? (row.regularPriceTtc / 1000).toFixed(3) : '';
  const currentSaleDinar = row.salePriceTtc ? (row.salePriceTtc / 1000).toFixed(3) : '';
  const initialMode = row.salePriceTtc && row.salePriceTtc > 0 ? 'sale' : 'regular';

  const [mode, setMode] = useState<'regular' | 'sale'>(initialMode);
  const [regularInput, setRegularInput] = useState<string>(currentRegDinar);
  const [saleInput, setSaleInput] = useState<string>(currentSaleDinar);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Valeur ciblée actuelle en dinars
  const parsedReg = parseFloat(regularInput.replace(',', '.'));
  const parsedSale = parseFloat(saleInput.replace(',', '.'));
  const effectiveDinar =
    mode === 'sale'
      ? (!Number.isNaN(parsedSale) && parsedSale > 0 ? parsedSale : null)
      : (!Number.isNaN(parsedReg) && parsedReg > 0 ? parsedReg : null);

  // Simulation d'écart en direct
  let simGapTnd: number | null = null;
  let simGapPct: number | null = null;
  let simBadge: CompetitorBadge | null = null;

  if (effectiveDinar !== null && row.cheapest !== null && row.cheapest > 0) {
    const effectiveMillimes = Math.round(effectiveDinar * 1000);
    simGapTnd = effectiveMillimes - row.cheapest;
    simGapPct = Math.round(((simGapTnd) / row.cheapest) * 1000) / 10;
    simBadge = simGapPct <= 0 ? 'leader' : simGapPct <= 2 ? 'aligned' : 'above';
  }

  const cheapestDinar = row.cheapest !== null ? (row.cheapest / 1000).toFixed(3) : null;
  const minusHalfDinar =
    row.cheapest !== null && row.cheapest > 500 ? ((row.cheapest - 500) / 1000).toFixed(3) : null;
  const minusOneDinar =
    row.cheapest !== null && row.cheapest > 1000 ? ((row.cheapest - 1000) / 1000).toFixed(3) : null;

  const applyQuickPrice = (dinarStr: string) => {
    if (mode === 'sale') {
      setSaleInput(dinarStr);
    } else {
      setRegularInput(dinarStr);
    }
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'regular') {
      if (Number.isNaN(parsedReg) || parsedReg <= 0) {
        setError('Veuillez saisir un prix normal valide (> 0 DT).');
        return;
      }
    } else {
      if (Number.isNaN(parsedSale) || parsedSale <= 0) {
        setError('Veuillez saisir un prix promotionnel valide (> 0 DT).');
        return;
      }
      const regBaseline = !Number.isNaN(parsedReg) && parsedReg > 0 ? parsedReg : (row.regularPriceTtc ? row.regularPriceTtc / 1000 : 0);
      if (regBaseline > 0 && parsedSale >= regBaseline) {
        setError(`Le prix promo (${parsedSale.toFixed(3)} DT) doit être strictement inférieur au prix normal (${regBaseline.toFixed(3)} DT).`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload: {
        regularPrice?: number | null;
        salePrice?: number | null;
        clearSale?: boolean;
      } = {};

      if (mode === 'regular') {
        payload.regularPrice = parsedReg;
      } else {
        payload.salePrice = parsedSale;
        if (!Number.isNaN(parsedReg) && parsedReg > 0) {
          payload.regularPrice = parsedReg;
        }
      }

      const res = await updateCompetitorProductPrice(row.productId, payload);
      if (res.ok) {
        onSuccess(res.product);
      } else {
        setError(res.message || 'Échec de mise à jour.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur réseau ou WooCommerce.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearSale = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await updateCompetitorProductPrice(row.productId, { clearSale: true });
      if (res.ok) {
        onSuccess(res.product);
      } else {
        setError(res.message || 'Échec de suppression de la promo.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur réseau ou WooCommerce.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Modal */}
        <div className="flex items-start justify-between border-b border-border bg-cream/40 px-6 py-4">
          <div className="flex items-center gap-3">
            {row.image ? (
              <img
                src={row.image}
                alt={row.name}
                className="h-12 w-12 rounded-lg border border-border object-contain bg-white p-0.5 shrink-0"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg border border-border bg-card flex items-center justify-center shrink-0 text-ink-ghost">
                <Tag className="h-5 w-5" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-accent">Console de Tarification</span>
                {row.wcProductId && (
                  <span className="rounded bg-cream px-1.5 py-0.5 font-mono text-[10px] text-ink-soft border border-border">
                    Woo #{row.wcProductId}
                  </span>
                )}
              </div>
              <h2 className="text-base font-bold text-ink line-clamp-1">{row.name}</h2>
              <div className="flex items-center gap-2 text-xs text-ink-soft">
                <span className="font-mono text-[11px]">{row.sku}</span>
                {row.brand && <span>· {row.brand}</span>}
                {row.gtinCanonical && (
                  <span className="inline-flex items-center gap-1 font-mono text-[11px] text-ink-ghost">
                    <Barcode className="h-3 w-3" /> {row.gtinCanonical}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-ink-ghost hover:bg-cream hover:text-ink transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Diagnostic marché actuel */}
          <div className="grid grid-cols-3 gap-2.5 rounded-xl border border-border bg-cream/30 p-3 text-center">
            <div>
              <div className="text-[10px] uppercase font-bold text-ink-ghost">Notre Prix Actuel</div>
              <div className="mt-0.5 text-base font-black tabular-nums text-ink">
                {formatMillimes(row.ourPrice)}
              </div>
              {row.salePriceTtc && row.regularPriceTtc && (
                <div className="text-[10px] text-ink-ghost line-through">
                  {formatMillimes(row.regularPriceTtc)}
                </div>
              )}
            </div>
            <div className="border-x border-border/80">
              <div className="text-[10px] uppercase font-bold text-ink-ghost">
                Moins cher ({row.cheapestSourceKey ? SHORT_LABELS[row.cheapestSourceKey] ?? row.cheapestSourceKey : 'marché'})
              </div>
              <div className="mt-0.5 text-base font-black tabular-nums text-emerald-800">
                {formatMillimes(row.cheapest)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-ink-ghost">Écart Actuel</div>
              <div
                className={cn(
                  'mt-0.5 text-base font-black tabular-nums',
                  (row.gapPct ?? 0) > 0 ? 'text-rose-700' : 'text-emerald-700'
                )}
              >
                {pct(row.gapPct)}
              </div>
              <div className="text-[10px] text-ink-ghost">
                {formatSignedGap(row.gapTnd)}
              </div>
            </div>
          </div>

          {/* Quick-Action Buttons */}
          {row.cheapest !== null && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-ink-soft">
                <Sparkles className="h-3.5 w-3.5 text-accent" />
                <span>Actions Rapides d'Alignement Marché</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {cheapestDinar && (
                  <button
                    type="button"
                    onClick={() => applyQuickPrice(cheapestDinar)}
                    className="flex flex-col items-center justify-center rounded-lg border border-sky-200 bg-sky-50/60 p-2 text-xs font-semibold text-sky-900 hover:bg-sky-100 hover:border-sky-300 transition-all text-center"
                  >
                    <span className="text-[10px] uppercase font-bold text-sky-700">S'aligner</span>
                    <span className="font-bold tabular-nums">{cheapestDinar} DT</span>
                  </button>
                )}
                {minusHalfDinar && (
                  <button
                    type="button"
                    onClick={() => applyQuickPrice(minusHalfDinar)}
                    className="flex flex-col items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300 transition-all text-center"
                  >
                    <span className="text-[10px] uppercase font-bold text-emerald-700">-0.500 DT</span>
                    <span className="font-bold tabular-nums">{minusHalfDinar} DT</span>
                  </button>
                )}
                {minusOneDinar && (
                  <button
                    type="button"
                    onClick={() => applyQuickPrice(minusOneDinar)}
                    className="flex flex-col items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300 transition-all text-center"
                  >
                    <span className="text-[10px] uppercase font-bold text-emerald-700">-1.000 DT</span>
                    <span className="font-bold tabular-nums">{minusOneDinar} DT</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Mode Selector: Prix Normal vs Prix Promo */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-ink-soft">Mode de modification</div>
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-cream/60 border border-border">
              <button
                type="button"
                onClick={() => setMode('regular')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-bold transition-all',
                  mode === 'regular'
                    ? 'bg-card text-ink shadow-xs border border-border/80'
                    : 'text-ink-soft hover:text-ink'
                )}
              >
                <Tag className="h-3.5 w-3.5" />
                <span>Modifier Prix Normal</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('sale')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-bold transition-all',
                  mode === 'sale'
                    ? 'bg-card text-accent shadow-xs border border-border/80'
                    : 'text-ink-soft hover:text-ink'
                )}
              >
                <Percent className="h-3.5 w-3.5" />
                <span>Appliquer Prix Promo</span>
              </button>
            </div>
          </div>

          {/* Inputs Section */}
          <div className="space-y-3">
            {mode === 'regular' ? (
              <div>
                <label className="block text-xs font-bold text-ink mb-1">
                  Nouveau Prix Normal TTC (en Dinars Tunisiens)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={regularInput}
                    onChange={(e) => {
                      setRegularInput(e.target.value);
                      setError(null);
                    }}
                    placeholder="ex: 35.000"
                    className="w-full rounded-xl border border-border bg-card px-3.5 py-2.5 text-base font-black tabular-nums text-ink focus:border-accent focus:ring-1 focus:ring-accent"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 font-bold text-xs text-ink-ghost">
                    DT
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-ink-soft">
                  Modifie la référence tarifaire catalogue (`regular_price`) directement sur votre boutique.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-ink mb-1">
                    Prix Promotionnel TTC (en Dinars Tunisiens)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={saleInput}
                      onChange={(e) => {
                        setSaleInput(e.target.value);
                        setError(null);
                      }}
                      placeholder="ex: 32.500"
                      className="w-full rounded-xl border border-border bg-card px-3.5 py-2.5 text-base font-black tabular-nums text-accent focus:border-accent focus:ring-1 focus:ring-accent"
                    />
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 font-bold text-xs text-accent">
                      DT PROMO
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-ink-soft mb-1">
                    Prix Normal de base (conservé barré sur le site)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={regularInput}
                      onChange={(e) => setRegularInput(e.target.value)}
                      placeholder="ex: 40.000"
                      className="w-full rounded-xl border border-border bg-cream/30 px-3.5 py-2 text-sm font-bold tabular-nums text-ink-soft"
                    />
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 font-medium text-xs text-ink-ghost">
                      DT (Normal)
                    </span>
                  </div>
                </div>

                {row.salePriceTtc && row.salePriceTtc > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClearSale}
                    disabled={isSubmitting}
                    className="w-full text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                  >
                    Supprimer la promo (Revenir au prix normal de {formatMillimes(row.regularPriceTtc)})
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Dynamic Simulation Banner */}
          {effectiveDinar !== null && simGapPct !== null && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3.5 text-xs space-y-1">
              <div className="font-bold text-sky-950 flex items-center justify-between">
                <span>Simulation après publication :</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-bold border',
                    badgeClass(simBadge)
                  )}
                >
                  {badgeLabel(simBadge)}
                </span>
              </div>
              <div className="flex items-center justify-between text-ink-soft">
                <span>Nouveau prix public :</span>
                <span className="font-bold text-ink tabular-nums">{effectiveDinar.toFixed(3)} DT</span>
              </div>
              <div className="flex items-center justify-between text-ink-soft">
                <span>Nouvel écart marché :</span>
                <span
                  className={cn(
                    'font-bold tabular-nums',
                    simGapPct > 0 ? 'text-rose-700' : 'text-emerald-700'
                  )}
                >
                  {pct(simGapPct)} ({formatSignedGap(simGapTnd)})
                </span>
              </div>
            </div>
          )}

          {/* Error notice */}
          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold text-rose-800">
              <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || effectiveDinar === null}
              className="gap-2 bg-accent text-white hover:bg-accent/90"
            >
              {isSubmitting ? (
                <span>Mise à jour en cours…</span>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Enregistrer & Pousser sur WooCommerce</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Ligne de Produit dans le Tableau
// ---------------------------------------------------------------------------

function ProductRow({
  row,
  sources,
  onOpenAdjust,
  isDistributor,
}: {
  row: CompetitorRow;
  sources: readonly CompetitorSourceMeta[];
  onOpenAdjust: (row: CompetitorRow) => void;
  isDistributor?: boolean;
}): React.ReactElement {
  const isPromo = typeof row.salePriceTtc === 'number' && row.salePriceTtc > 0;

  return (
    <Tr className="hover:bg-cream/20 transition-colors">
      {/* 1. Colonne Produit */}
      <Td className="max-w-xs">
        <div className="flex items-center gap-3">
          {row.image ? (
            <img
              src={row.image}
              alt={row.name}
              className="h-16 w-16 rounded-xl border border-border object-contain bg-white p-1 shrink-0 shadow-xs"
              loading="lazy"
            />
          ) : (
            <div className="h-16 w-16 rounded-xl border border-border bg-card flex items-center justify-center shrink-0 text-ink-ghost">
              <Tag className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-ink text-sm leading-snug line-clamp-1" title={row.name}>
              {row.name}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
              <span className="font-mono text-[11px] text-ink-ghost bg-cream px-1.5 py-0.2 rounded border border-border">
                {row.sku}
              </span>
              {row.brand && (
                <span className="text-[11px] font-semibold text-ink-soft">
                  {row.brand}
                </span>
              )}
              {row.gtinCanonical && (
                <span
                  title={`Code-barres canonique : ${row.gtinCanonical}`}
                  className="inline-flex items-center gap-1 font-mono text-[10px] text-ink-ghost bg-card px-1 py-0.2 rounded border border-border"
                >
                  <Barcode className="h-3 w-3" />
                  {row.gtinCanonical}
                </span>
              )}
              {row.wcProductId && (
                <span className="font-mono text-[10px] text-accent/80">
                  #{row.wcProductId}
                </span>
              )}
            </div>
          </div>
        </div>
      </Td>

      {/* 2. Colonne Notre Prix TTC */}
      <Td className="text-right tabular-nums whitespace-nowrap">
        {isPromo ? (
          <div>
            <div className="flex items-center justify-end gap-1.5">
              <span className="text-[9px] font-black uppercase text-accent bg-accent/10 px-1 py-0.2 rounded border border-accent/20">
                PROMO
              </span>
              <span className="font-black text-accent text-sm">
                {formatMillimes(row.salePriceTtc)}
              </span>
            </div>
            <div className="text-[11px] text-ink-ghost line-through">
              {formatMillimes(row.regularPriceTtc)}
            </div>
          </div>
        ) : (
          <div className="font-black text-ink text-sm">
            {formatMillimes(row.ourPrice)}
          </div>
        )}
      </Td>

      {/* 3. Colonne Verdict */}
      <Td className="whitespace-nowrap">
        <span
          className={cn(
            'inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-bold',
            badgeClass(row.badge)
          )}
        >
          {badgeLabel(row.badge)}
        </span>
      </Td>

      {/* 4. Colonne Écart % & DT */}
      <Td
        className={cn(
          'text-right font-bold tabular-nums whitespace-nowrap',
          (row.gapPct ?? 0) > 0 ? 'text-rose-700' : 'text-emerald-700'
        )}
      >
        <div className="text-sm">{pct(row.gapPct)}</div>
        {row.gapTnd !== null && (
          <span className="block text-[11px] font-medium text-ink-ghost">
            {formatSignedGap(row.gapTnd)}
          </span>
        )}
      </Td>

      {/* 5. Colonnes Prix par Concurrent */}
      {sources.map((s) => {
        const found = row.prices.find((p) => p.sourceKey === s.key);
        const isCheapestHere = found && row.cheapest !== null && found.price === row.cheapest;

        return (
          <Td key={s.key} className="text-right tabular-nums whitespace-nowrap">
            {found ? (
              <div className="space-y-0.5">
                <div className="flex items-center justify-end gap-1">
                  {found.url ? (
                    <a
                      href={found.url}
                      target="_blank"
                      rel="noreferrer"
                      title="Voir la fiche chez ce concurrent"
                      className="font-bold text-ink hover:text-accent hover:underline flex items-center gap-1"
                    >
                      <span>{formatMillimes(found.price)}</span>
                      <ExternalLink className="h-3 w-3 text-ink-ghost inline shrink-0" />
                    </a>
                  ) : (
                    <span className="font-bold text-ink">{formatMillimes(found.price)}</span>
                  )}
                  {isCheapestHere && (
                    <span title="Meilleur prix marché" className="text-[10px]">
                      👑
                    </span>
                  )}
                </div>
                <div className="text-[11px]">
                  <VsOurs ours={row.ourPrice} theirs={found.price} />
                </div>
              </div>
            ) : (
              <span className="text-ink-ghost/60">—</span>
            )}
          </Td>
        );
      })}

      {/* 6. Colonne Moins Cher Global */}
      <Td className="text-right tabular-nums whitespace-nowrap">
        <div className="font-black text-emerald-900 text-sm">
          {formatMillimes(row.cheapest)}
        </div>
        {row.cheapestSourceKey && (
          <span className="block text-[10px] font-semibold text-ink-soft">
            {SHORT_LABELS[row.cheapestSourceKey] ?? row.cheapestSourceKey}
          </span>
        )}
      </Td>

      {/* 7. Colonne Action Immédiate (masquée pour les distributeurs) */}
      {!isDistributor && (
        <Td className="text-right whitespace-nowrap">
          <Button
            variant="soft"
            size="sm"
            onClick={() => onOpenAdjust(row)}
            className="gap-1.5 text-xs font-bold text-accent hover:bg-accent/10 border-accent/20"
          >
            <PencilLine className="h-3.5 w-3.5" />
            <span>Corriger</span>
          </Button>
        </Td>
      )}
    </Tr>
  );
}

// ---------------------------------------------------------------------------
// Page Principale : Console Prix Concurrents
// ---------------------------------------------------------------------------

type GapFilter =
  | 'all'
  | 'plus_10'
  | 'plus_20'
  | 'plus_30'
  | 'plus_over_30'
  | 'minus_10'
  | 'minus_20'
  | 'minus_30'
  | 'minus_over_30';

export function CompetitorPricesPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: me });
  const isDistributor = Boolean(meQuery.data?.brandScope);
  const distributorBrands = meQuery.data?.brandScope
    ? meQuery.data.brandScope.split(/[,;]+/).map((b) => b.trim()).filter(Boolean)
    : [];

  const [search, setSearch] = useState<string>('');
  const [debounced, setDebounced] = useState<string>('');
  const [brand, setBrand] = useState<string>('');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'above' | 'aligned' | 'leader'>('all');
  const [gapFilter, setGapFilter] = useState<'all' | 'plus_10' | 'plus_20' | 'plus_30' | 'plus_over_30' | 'minus_10' | 'minus_20' | 'minus_30' | 'minus_over_30'>('all');
  const [minGap, setMinGap] = useState<string>('0');
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [selectedProductForAdjust, setSelectedProductForAdjust] = useState<CompetitorRow | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  const perPage = 50;

  // Déterminer le minGapPct envoyé à l'API selon le statut sélectionné
  const minGapPctParam =
    statusFilter === 'above'
      ? Math.max(0, Number(minGap) || 0)
      : undefined;

  const query = useQuery({
    queryKey: ['competitor-prices', debounced, brand, excludedSubBrands, statusFilter, minGapPctParam, sortAsc, page],
    queryFn: () =>
      fetchCompetitorPrices({
        search: debounced || undefined,
        brand: brand || undefined,
        excludedSubBrands: excludedSubBrands.length > 0 ? excludedSubBrands.join(',') : undefined,
        minGapPct: minGapPctParam,
        sort: sortAsc ? 'gap_asc' : 'gap_desc',
        page,
        perPage,
      }),
    staleTime: 60_000,
  });

  const data = query.data;
  const totals = data?.totals;
  const sources = data?.sources ?? [];

  // Filtrage local additionnel pour les statuts 'leader' ou 'aligned' si sélectionné
  let displayedRows = data?.rows ?? [];
  if (statusFilter === 'leader') {
    displayedRows = displayedRows.filter((r) => r.badge === 'leader');
  } else if (statusFilter === 'aligned') {
    displayedRows = displayedRows.filter((r) => r.badge === 'aligned');
  }

  // Filtrage dynamique par tranche d'écart %
  if (gapFilter === 'plus_10') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct >= 10);
  } else if (gapFilter === 'plus_20') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct >= 20);
  } else if (gapFilter === 'plus_30') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct >= 30);
  } else if (gapFilter === 'plus_over_30') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct > 30);
  } else if (gapFilter === 'minus_10') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct <= -10);
  } else if (gapFilter === 'minus_20') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct <= -20);
  } else if (gapFilter === 'minus_30') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct <= -30);
  } else if (gapFilter === 'minus_over_30') {
    displayedRows = displayedRows.filter((r) => r.gapPct !== null && r.gapPct < -30);
  }

  const exportUrl = (): string => {
    const q = new URLSearchParams();
    if (debounced) q.set('search', debounced);
    if (brand) q.set('brand', brand);
    if (statusFilter === 'above') q.set('minGapPct', String(Number(minGap) || 0));
    q.set('sort', sortAsc ? 'gap_asc' : 'gap_desc');
    return `/api/v1/prices/competitors/export.xlsx?${q.toString()}`;
  };

  const handlePriceUpdateSuccess = (updated: {
    id: number;
    regularPriceTtc: number | null;
    salePriceTtc: number | null;
    ourPrice: number | null;
  }) => {
    setSelectedProductForAdjust(null);
    setSuccessToast(
      `Prix mis à jour avec succès : nouveau tarif public de ${formatMillimes(updated.ourPrice)}`
    );
    // Invalider et rafraîchir les comparaisons
    queryClient.invalidateQueries({ queryKey: ['competitor-prices'] });
    setTimeout(() => setSuccessToast(null), 5000);
  };

  const renderPagination = (position: 'top' | 'bottom') => {
    if (!data) return null;
    const start = data.pagination.total === 0 ? 0 : (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, data.pagination.total);

    return (
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-cream/40 rounded-xl border border-border shadow-2xs text-xs',
          position === 'top' ? 'mb-2.5' : 'mt-3'
        )}
      >
        <div className="text-ink-soft">
          Affichage de <strong className="text-ink">{start}</strong> à <strong className="text-ink">{end}</strong> sur{' '}
          <strong className="text-ink">{data.pagination.total}</strong> produits appariés
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={cn(
              'px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all border shadow-xs',
              page <= 1
                ? 'opacity-40 cursor-not-allowed bg-slate-100 text-slate-400 border-slate-200'
                : 'bg-white hover:bg-slate-50 text-slate-900 border-slate-300 hover:border-slate-400 active:scale-95'
            )}
          >
            ← Précédent
          </button>
          <span className="px-3 py-1 rounded-lg text-xs font-bold bg-herb-700 text-white shadow-xs">
            Page {data.pagination.page} / {Math.max(1, data.pagination.totalPages)}
          </span>
          <button
            type="button"
            disabled={page >= data.pagination.totalPages}
            onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
            className={cn(
              'px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all border shadow-xs',
              page >= data.pagination.totalPages
                ? 'opacity-40 cursor-not-allowed bg-slate-100 text-slate-400 border-slate-200'
                : 'bg-white hover:bg-slate-50 text-slate-900 border-slate-300 hover:border-slate-400 active:scale-95'
            )}
          >
            Suivant →
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* En-tête de la Console */}
      <PageHeader
        index={isDistributor ? 'PORTAIL' : 'S27'}
        eyebrow={isDistributor ? 'PORTAIL DISTRIBUTEUR · SURVEILLANCE MARCHÉ' : 'INTELLIGENCE TARIFAIRE & CONSOLE PRIX'}
        title="Console Prix Concurrents"
        purpose={
          isDistributor
            ? `Surveillance en temps réel des prix de vente TTC face aux concurrents de la place tunisienne pour vos marques (${distributorBrands.join(', ')}). Données actualisées en continu.`
            : 'Surveillance en temps réel des prix de vente TTC face aux concurrents de la place tunisienne. Détectez instantanément les écarts et corrigez vos prix directement sur WooCommerce.'
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              className="gap-1.5 text-xs font-semibold"
            >
              <RotateCcw className={cn('h-3.5 w-3.5', query.isFetching ? 'animate-spin' : '')} />
              <span>Actualiser</span>
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={() => window.open(exportUrl(), '_blank')}
              disabled={!totals || totals.products === 0}
              className="gap-1.5 text-xs font-semibold"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Export Excel</span>
            </Button>
          </div>
        }
      />

      {/* Notification Toast de Succès */}
      {successToast && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-900 shadow-sm animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <span>{successToast}</span>
          </div>
          <button
            onClick={() => setSuccessToast(null)}
            className="text-emerald-700 hover:text-emerald-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* KPI Cards / Cartes Synthèse */}
      {totals && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <KpiCard
            label="Produits Comparés"
            value={int(totals.products)}
            sub="appariés confirmed + GTIN canonique"
            accent="text-ink"
            icon={<Layers className="h-4 w-4 text-ink-ghost" />}
            active={statusFilter === 'all'}
            onClick={() => {
              setStatusFilter('all');
              setPage(1);
            }}
          />
          <KpiCard
            label="⚠️ Plus Chers (Au-dessus)"
            value={`${int(totals.aboveCount)} · ${share(totals.aboveCount, totals.products)}`}
            sub={`écart moyen ${totals.avgGapPctAbove === null ? '—' : `+${totals.avgGapPctAbove} %`}`}
            accent="text-rose-700"
            icon={<TrendingUp className="h-4 w-4 text-rose-500" />}
            active={statusFilter === 'above'}
            onClick={() => {
              setStatusFilter('above');
              setPage(1);
            }}
          />
          <KpiCard
            label="🎯 Alignés (±2 %)"
            value={`${int(totals.alignedCount)} · ${share(totals.alignedCount, totals.products)}`}
            sub="prix dans la tolérance marché"
            accent="text-sky-800"
            icon={<span className="text-xs">🎯</span>}
            active={statusFilter === 'aligned'}
            onClick={() => {
              setStatusFilter('aligned');
              setPage(1);
            }}
          />
          <KpiCard
            label="🏆 Moins Chers (Leaders)"
            value={`${int(totals.leaderCount)} · ${share(totals.leaderCount, totals.products)}`}
            sub="notre prix est le plus bas"
            accent="text-emerald-800"
            icon={<TrendingDown className="h-4 w-4 text-emerald-500" />}
            active={statusFilter === 'leader'}
            onClick={() => {
              setStatusFilter('leader');
              setPage(1);
            }}
          />
        </div>
      )}

      {/* Barre d'outils et de filtrage */}
      <Panel className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Recherche & Filtre Marque */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
              <Search className="h-4 w-4 text-ink-ghost" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setDebounced(search.trim());
                }}
                onBlur={() => setDebounced(search.trim())}
                placeholder="Produit, SKU, GTIN… (Entrée)"
                className="w-64 rounded-xl border border-border bg-card px-3 py-1.5 text-sm text-ink placeholder:text-ink-ghost"
              />
            </label>

            {isDistributor ? (
              <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
                <span>Marque</span>
                <select
                  value={brand}
                  onChange={(e) => {
                    setBrand(e.target.value);
                    setExcludedSubBrands([]);
                    setPage(1);
                  }}
                  className="rounded-xl border border-border bg-card px-3 py-1.5 text-sm font-semibold text-ink"
                >
                  <option value="">Toutes mes marques ({distributorBrands.length})</option>
                  {distributorBrands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
                <span>Marque</span>
                <input
                  value={brand}
                  onChange={(e) => {
                    setBrand(e.target.value);
                    setExcludedSubBrands([]);
                    setPage(1);
                  }}
                  placeholder="ex: SVR, Avene…"
                  className="w-40 rounded-xl border border-border bg-card px-3 py-1.5 text-sm text-ink placeholder:text-ink-ghost"
                />
              </label>
            )}

            {/* Filtre par tranche d'écart */}
            <label className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft">
              <span>Écart :</span>
              <select
                value={gapFilter}
                onChange={(e) => {
                  setGapFilter(e.target.value as GapFilter);
                  setPage(1);
                }}
                className={cn(
                  'rounded-xl border px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer',
                  gapFilter.startsWith('plus')
                    ? 'border-rose-300 bg-rose-50 text-rose-900 ring-1 ring-rose-300'
                    : gapFilter.startsWith('minus')
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-300'
                      : 'border-border bg-card text-ink'
                )}
              >
                <option value="all">Tous les écarts</option>
                <optgroup label="Plus chers que concurrents (Écart +)">
                  <option value="plus_10">+10 % et plus</option>
                  <option value="plus_20">+20 % et plus</option>
                  <option value="plus_30">+30 % et plus</option>
                  <option value="plus_over_30">Plus de +30 %</option>
                </optgroup>
                <optgroup label="Moins chers que concurrents (Écart -)">
                  <option value="minus_10">-10 % et moins</option>
                  <option value="minus_20">-20 % et moins</option>
                  <option value="minus_30">-30 % et moins</option>
                  <option value="minus_over_30">Plus de -30 %</option>
                </optgroup>
              </select>
            </label>

            {/* Si filtre 'Plus chers' actif, seuil mini % */}
            {statusFilter === 'above' && (
              <label className="flex items-center gap-2 text-xs font-semibold text-rose-800 bg-rose-50 border border-rose-200 rounded-xl px-2.5 py-1">
                <span>Écart mini :</span>
                <input
                  value={minGap}
                  onChange={(e) => {
                    setMinGap(e.target.value);
                    setPage(1);
                  }}
                  inputMode="decimal"
                  className="w-12 rounded-lg border border-rose-300 bg-card px-1.5 py-0.5 font-mono text-xs text-rose-900 text-center"
                />
                <span>%</span>
              </label>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortAsc((v) => !v)}
              className="gap-1.5 text-xs font-semibold"
              title="Inverser le tri par écart de prix"
            >
              <ArrowDownWideNarrow className="h-3.5 w-3.5" />
              <span>Écart {sortAsc ? 'Croissant (↑)' : 'Décroissant (↓)'}</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearch('');
                setDebounced('');
                setBrand('');
                setExcludedSubBrands([]);
                setStatusFilter('all');
                setMinGap('0');
                setSortAsc(false);
                setPage(1);
              }}
              className="gap-1.5 text-xs font-semibold text-ink-ghost"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Réinitialiser</span>
            </Button>
          </div>

          {/* Sub-brands & ranges checkbox pills */}
          <SubBrandPills
            brand={brand}
            excludedSubBrands={excludedSubBrands}
            onChange={(ex) => {
              setExcludedSubBrands(ex);
              setPage(1);
            }}
          />

          {/* Sources actives et fraîcheur */}
          <div className="flex items-center gap-2 text-xs text-ink-ghost">
            <span>Sources :</span>
            <div className="flex flex-wrap gap-1">
              {sources.map((s) => (
                <span
                  key={s.key}
                  title={`${s.label}${s.lastCrawlAt ? ` — dernier crawl : ${s.lastCrawlAt.slice(0, 10)}` : ' — jamais crawlé'}`}
                  className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-800 border border-slate-300 shadow-2xs"
                >
                  {shortLabel(s)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      {/* État de chargement / erreur */}
      {query.isPending && (
        <Panel className="p-8 text-center text-sm font-semibold text-ink-soft animate-pulse">
          Chargement de la console des prix concurrents…
        </Panel>
      )}

      {query.isError && (
        <Panel className="p-6 text-sm font-semibold text-rose-700 border-rose-200 bg-rose-50/50">
          Échec du chargement des comparaisons ({query.error instanceof Error ? query.error.message : 'erreur inconnue'}).
        </Panel>
      )}

      {/* Tableau Console Haute Densité */}
      {data && (
        <>
          {renderPagination('top')}
          <TableShell>
            <Table>
              <THead>
                <Tr>
                  <Th className="w-80">Produit & Références</Th>
                  <Th className="text-right">
                    <span title="Notre prix de vente TTC (affiche le prix promo actif si promotion en cours)">Notre TTC</span>
                  </Th>
                  <Th>Verdict Marché</Th>
                  <Th className="text-right">Écart</Th>
                  {sources.map((s) => (
                    <Th key={s.key} className="text-right">
                      <span
                        title={`${s.label}${s.lastCrawlAt ? ` — dernier crawl : ${s.lastCrawlAt.slice(0, 10)}` : ' — non crawlé'}`}
                      >
                        {shortLabel(s)}
                      </span>
                    </Th>
                  ))}
                  <Th className="text-right">Moins Cher</Th>
                  {!isDistributor && <Th className="text-right w-28">Action</Th>}
                </Tr>
              </THead>
              <TBody>
                {displayedRows.map((row) => (
                  <ProductRow
                    key={row.productId}
                    row={row}
                    sources={sources}
                    isDistributor={isDistributor}
                    onOpenAdjust={(targetRow) => setSelectedProductForAdjust(targetRow)}
                  />
                ))}
                {displayedRows.length === 0 && (
                  <Tr>
                    <Td colSpan={(isDistributor ? 5 : 6) + sources.length} className="text-center py-8 text-ink-ghost">
                      Aucun produit ne correspond aux filtres appliqués.
                    </Td>
                  </Tr>
                )}
              </TBody>
            </Table>
          </TableShell>

{renderPagination('bottom')}

          <div className="rounded-xl border border-border/80 bg-cream/30 p-3 text-[11px] text-ink-soft space-y-1">
            <p>
              <strong>Règles de la Console :</strong> Tous les prix affichés sont en <strong>TTC</strong> (décision client : parashop et tous concurrents comparés en TTC strict). Appariement exclusif sur preuves : décisions de matching confirmées ou égalité stricte sur le code-barres canonique approuvé.
            </p>
            <p className="text-ink-ghost">
              Le pourcentage sous chaque concurrent indique la différence de son prix par rapport au nôtre (vert = nous sommes moins chers que lui).
              {!isDistributor && " Le bouton « Corriger » permet de modifier le prix normal ou d'appliquer une promo poussée immédiatement sur WooCommerce."}
            </p>
          </div>
        </>
      )}

      {/* Modale d'ajustement si un produit est sélectionné (désactivée pour distributeur) */}
      {!isDistributor && selectedProductForAdjust && (
        <AdjustPriceModal
          row={selectedProductForAdjust}
          onClose={() => setSelectedProductForAdjust(null)}
          onSuccess={handlePriceUpdateSuccess}
        />
      )}
    </div>
  );
}
